import classNames from 'classnames';
import Immutable from 'immutable';
import $ from 'jquery';
import React from 'react';
import _ from 'underscore';

import Spinner from 'sharedreact/components/Spinner';
import TableHeader from 'sharedreact/components/TableHeader';
import TableRow, { RowType } from 'sharedreact/components/TableRow';
import SortOrder from 'sharedreact/data/SortOrder';
import type TableColumn from 'sharedreact/data/TableColumn';
import TableSortPreferences from 'sharedreact/data/TableSortPreferences';
import type { RangeType } from 'sharedreact/stores/generic/Type';
import { translate } from 'sharedreact/utils/LanguageSupport';

function normalizeColumnWidths<T extends RowType> (tableColumns: Immutable.List<TableColumn<T>>): Immutable.List<string> {
  let sumOfWidthPcs: number = 0;
  let count: number = 0;
  for (const tableColumn of tableColumns) {
    const width: string | undefined = tableColumn.width?.trim();
    if (width != null && width.endsWith('%')) {
      count++;
      const widthPc = parseFloat(width);
      sumOfWidthPcs += widthPc;
    }
  }

  let sumOfWidthPcs2: number = 0;
  let count2: number = 0;
  return tableColumns.map((tableColumn: TableColumn<T>) => {
    let width: string | undefined = tableColumn.width?.trim();

    if (width != null && width.endsWith('%') && sumOfWidthPcs > 0 && sumOfWidthPcs !== 100) {
      count2++;
      let widthPc: number;
      if (count2 < count) {
        widthPc = parseFloat(width);
        widthPc = (widthPc / sumOfWidthPcs) * 100;
        widthPc = Math.round(widthPc * 100) / 100;
        sumOfWidthPcs2 += widthPc;
      } else {
        widthPc = 100 - sumOfWidthPcs2;
      }
      width = widthPc + '%';
    }

    return width ?? '';
  });
}

function getMinimumColumnWidthPercentages<T extends RowType> (tableColumns: Immutable.List<TableColumn<T>>, tableWidth: number): Immutable.List<number> {
  const defaultMinColWidthPc: number = 7;

  return tableColumns.map((tableColumn: TableColumn<T>) => {
    let minimumWidth: string | undefined = tableColumn.minimumWidth;

    if (minimumWidth == null) {
      return defaultMinColWidthPc;
    }

    minimumWidth = minimumWidth.trim();

    if (minimumWidth.endsWith('%')) {
      return parseFloat(minimumWidth);
    } else if (minimumWidth.endsWith('px')) {
      const minimumWidthPx = parseFloat(minimumWidth);
      return ((minimumWidthPx / tableWidth) * 100);
    } else {
      return defaultMinColWidthPc;
    }
  });
}

function applyMinimumColumnWidths (minColWidthPcs: Immutable.List<number>, columnWidths: Immutable.List<string>): Immutable.List<string> {
  let widthToRedistribute = 0;
  const columnReducibility: boolean[] = [];

  let columnWidthPcs: Immutable.List<number> = columnWidths.map((width: string, index: number) => {
    if (width.endsWith('%')) {
      const widthPc: number = parseFloat(width);
      const minColWidthPc: number = minColWidthPcs.get(index) ?? 0;
      if (widthPc < minColWidthPc) {
        widthToRedistribute += (minColWidthPc - widthPc);
        columnReducibility.push(false);
        return minColWidthPc;
      } else {
        if (widthPc === minColWidthPc) {
          columnReducibility.push(false);
        } else {
          columnReducibility.push(true);
        }
        return widthPc;
      }
    } else {
      columnReducibility.push(false);
      return -1;
    }
  });

  let reducibleColumnCount: number = columnReducibility.filter((red) => red).length;
  while (widthToRedistribute > 0 && reducibleColumnCount > 0) {
    const comp: number = widthToRedistribute / reducibleColumnCount;

    widthToRedistribute = 0;

    columnWidthPcs = columnWidthPcs.map((widthPc: number, index: number) => {
      if (columnReducibility[index]) {
        const retval: number = widthPc - comp;
        const minColWidthPc: number = minColWidthPcs.get(index) ?? 0;
        if (retval < minColWidthPc) {
          widthToRedistribute += (minColWidthPc - retval);
          columnReducibility[index] = false;
          return minColWidthPc;
        } else {
          return retval;
        }
      } else {
        return widthPc;
      }
    });

    reducibleColumnCount = columnReducibility.filter((red) => red).length;
  }

  const retval: Immutable.List<string> = columnWidthPcs.map((widthPc: number, index: number) => {
    if (widthPc === -1) {
      return columnWidths.get(index) ?? '';
    } else {
      const width = Math.round(widthPc * 100) / 100;
      return (width + '%');
    }
  });

  return retval;
}

function sumOfFixedWidthColumnWidths (columnWidths: Immutable.List<string>): number {
  let retval: number = 0;
  for (const width of columnWidths) {
    if (width.endsWith('px')) {
      retval += parseFloat(width);
    }
  }
  return retval;
}

function _isHighlighted (highlightId: string | Immutable.Set<string> | undefined, id: string): boolean {
  return (typeof (highlightId) === 'string'
    ? highlightId === id
    : (highlightId instanceof Immutable.Set)
       ? highlightId.has(id)
       : false);
}

type Props<T extends RowType> = {
  id: string,
  language: string,
  className?: string,
  tableColumns: Immutable.List<TableColumn<T>>,
  rowData: Immutable.List<T | null>,
  realTotalCount?: number,
  isInitialized?: boolean,
  isFetching?: boolean,
  selection?: Immutable.Set<string>,
  showSelection?: boolean,
  sortOrder?: SortOrder,
  ensureSelectionIsVisible?: boolean,
  selectionIndexes?: Immutable.Map<string, number>,
  emptyTableText?: string,
  loadingText?: string,
  rowHeight?: number,
  allTableColumns?: Immutable.List<TableColumn<T>>,
  sortPreferences?: TableSortPreferences,
  highlightId?: Immutable.Set<string> | string,
  highlightIndex?: Immutable.Set<number> | number,
  setFocus?: boolean,
  noTableHeader?: boolean,
  disabledIds?: Immutable.List<string>,

  isSelected?: (item: T, selection: Immutable.Set<string>) => boolean,
  setVisibleRange?: (range: RangeType) => void,
  onItemSelect?: (id: string) => void,
  onHover?: (sourceItem: any, targetItem: T, position: string) => void,
  onDrop?: (sourceItem: any, targetItem: T, position: string) => void,
  onSortOrderChange?: (sortOrder: SortOrder) => void,
  onEnsuredSelectionIsVisible?: () => void,
  onToggleColumnSelection?: (columnName: string) => void,
  onColumnWidthsChange?: (widths: Immutable.List<string>) => void,
  onMoveColumn?: (columnToMove: number, moveTo: number) => void,
  clearHighlightId?: (id: string) => void,
  getClassNameOnDragHover?: (sourceItem: any, targetItem: T, position: string) => string,
  itemCanDragDrop?: (item: T) => boolean,
  itemGetDragDropItemType?: (item: T) => string,
  itemAcceptsDropItemTypes?: (item: T) => string[],
};

type State = {
  headerWidth: string,
  columnWidths: Immutable.List<string>,
  reorderColumnActive: boolean
};

type DefaultProps = Required<Pick<Props<any>, 'selection' | 'isInitialized' | 'sortOrder' | 'ensureSelectionIsVisible' | 'selectionIndexes' | 'emptyTableText' | 'loadingText' | 'rowHeight' | 'setFocus' | 'disabledIds'>>;

type PropsWithDefaults<T extends RowType> = Omit<Props<T>, keyof DefaultProps> & DefaultProps;

class Table<T extends RowType> extends React.PureComponent<Props<T>, State> {
  static defaultProps: DefaultProps = {
    selection: Immutable.Set(),
    isInitialized: true,
    sortOrder: new SortOrder(),
    ensureSelectionIsVisible: false,
    selectionIndexes: Immutable.Map(),
    emptyTableText: 'shared.general.noitemsfound',
    loadingText: 'shared.general.isfetching',
    rowHeight: 53,
    setFocus: true,
    disabledIds: Immutable.List()
  };

  private visibleRange: RangeType;
  private headerRootElement: HTMLElement | null;
  private dataTableContainerElement: HTMLDivElement | null;
  private dataTableElement: HTMLTableElement | null;
  private scrollTimer: NodeJS.Timeout | null;
  private hoverColumnHeader: number;
  private hoverColumnHeaderArea: string;
  private resizeColumn: number;
  private resizeColumnStartX: number;
  private resizeColumnStartWidths: Immutable.List<number>;
  private columnWidths: Immutable.List<string>;
  private preventSortOrderChange: boolean;
  private selectionIndexes: Immutable.List<number>;

  private reorderColumn: number;
  private reorderColumnMouseStartX: number;
  private reorderColumnStartX: number;
  private reorderColumnWidth: number;
  private reorderColumnElement: HTMLDivElement | null;

  private sortPreferences: TableSortPreferences;

  private _setVisibleRange: ((range: RangeType) => void) | null;

  constructor (props: Props<T>) {
    super(props);

    const columnWidths: Immutable.List<string> = normalizeColumnWidths(props.tableColumns);

    this.state = {
      headerWidth: '100%',
      columnWidths: columnWidths,
      reorderColumnActive: false
    };

    this.headerRootElement = null;
    this.dataTableContainerElement = null;
    this.dataTableElement = null;
    this.scrollTimer = null;
    this.reorderColumnElement = null;

    this.visibleRange = {from: 0, until: 0};
    this.hoverColumnHeader = -1;
    this.hoverColumnHeaderArea = '';
    this.resizeColumn = -1;
    this.resizeColumnStartX = -1;
    this.resizeColumnStartWidths = Immutable.List();
    this.columnWidths = Immutable.List();
    this.preventSortOrderChange = false;
    this.selectionIndexes = Immutable.List();
    this.reorderColumn = -1;
    this.reorderColumnMouseStartX = -1;
    this.reorderColumnStartX = -1;
    this.reorderColumnWidth = 0;
    if (props.sortPreferences == null) {
      this.sortPreferences = new TableSortPreferences();
    } else {
      this.sortPreferences = props.sortPreferences;
    }

    const sortOrder = props.sortOrder ?? new SortOrder();

    if (sortOrder.keyword != null && sortOrder.keyword.length > 0) {
      this.sortPreferences = this.sortPreferences
        .set('activeKeyword', sortOrder.keyword)
        .setAscending(sortOrder.keyword, sortOrder.ascending);
    }

    this.updateHeaderWidth = this.updateHeaderWidth.bind(this);
    this.updateViewParams = this.updateViewParams.bind(this);
    this.updateHeaderCursor = this.updateHeaderCursor.bind(this);
    this.handleScroll = this.handleScroll.bind(this);
    this.handleRowClick = this.handleRowClick.bind(this);
    this.handleRowHover = this.handleRowHover.bind(this);
    this.handleRowDrop = this.handleRowDrop.bind(this);
    this.handleColumnHeaderMouseEnter = this.handleColumnHeaderMouseEnter.bind(this);
    this.handleColumnHeaderMouseLeave = this.handleColumnHeaderMouseLeave.bind(this);
    this.handleColumnHeaderMouseMove = this.handleColumnHeaderMouseMove.bind(this);
    this.handleColumnHeaderMouseDown = this.handleColumnHeaderMouseDown.bind(this);
    this.handleColumnHeaderClick = this.handleColumnHeaderClick.bind(this);
    this.handleMouseDown = this.handleMouseDown.bind(this);
    this.handleMouseMove = this.handleMouseMove.bind(this);
    this.handleMouseUp = this.handleMouseUp.bind(this);
    this.handleWindowResize = this.handleWindowResize.bind(this);


    this._setVisibleRange = this.props.setVisibleRange != null ? _.throttle(this.props.setVisibleRange, 50) : null;
  }

  _isSelected (item: any, selection: Immutable.Set<string>): boolean {
    if (selection.size > 0) {
      if (this.props.isSelected != null) {
        return this.props.isSelected(item, selection);
      } else {
        return selection.includes(item.id);
      }
    }

    return false;
  }

  updateHeaderWidth (): void {
    if (this.dataTableContainerElement != null && this.dataTableElement != null) {
      const containerWidth = this.dataTableContainerElement.offsetWidth;
      const tableWidth = this.dataTableElement?.offsetWidth;
      const headerWidthPc = (tableWidth / containerWidth) * 100;
      const headerWidth = headerWidthPc + '%';

      if (this.state.headerWidth !== headerWidth) {
        this.setState({
          headerWidth: headerWidth
        });
      }
    }
  }

  updateViewParams (): void {
    this._updateViewParams(false);
  }

  _updateViewParams (forceUpdate: boolean): void {
    if (this.dataTableContainerElement == null) {
      return;
    }

    const { rowHeight } = (this.props as PropsWithDefaults<T>);

    console.assert(rowHeight !== -1, '_updateViewParams called when no row height was set');

    const scrollTop: number = $(this.dataTableContainerElement).scrollTop() ?? 0;
    const containerHeight: number = this.dataTableContainerElement.offsetHeight ?? 0;

    const rowsAbove: number = Math.floor(scrollTop / rowHeight);
    const visibleRows: number = Math.ceil(containerHeight / rowHeight);

    // const visibleRange = {from: rowsAbove, until: rowsAbove + visibleRows - 1};
    // Get two more to be sure
    const visibleRange: RangeType = {from: rowsAbove, until: rowsAbove + visibleRows + 1};

    if (forceUpdate ||
      visibleRange.from !== this.visibleRange.from ||
      visibleRange.until !== this.visibleRange.until) {
      if (this._setVisibleRange != null) {
        const visibleRangeFunc: (range: RangeType) => void = this._setVisibleRange;
        setTimeout(() => visibleRangeFunc(visibleRange), 0);
      }
      this.visibleRange = visibleRange;

      this.forceUpdate();
    }
  }

  updateHeaderCursor (): void {
    const { tableColumns } = (this.props as PropsWithDefaults<T>);

    // Early out when resizing column
    if (this.resizeColumn >= 0) {
      return;
    }

    if (this.hoverColumnHeader >= 0) {
      if (this.hoverColumnHeaderArea === 'col-resize-left' || this.hoverColumnHeaderArea === 'col-resize-right') {
        $(this.headerRootElement!).find('table > thead').css('cursor', 'col-resize');
      } else {
        const tableColumn: TableColumn<T> | undefined = tableColumns.get(this.hoverColumnHeader);
        if (tableColumn == null || tableColumn.sortOrderKeyword == null || tableColumn.sortOrderKeyword.length === 0) {
          $(this.headerRootElement!).find('table > thead').css('cursor', 'default');
        } else {
          $(this.headerRootElement!).find('table > thead').css('cursor', 'pointer');
        }
      }
    } else {
      $(this.headerRootElement!).find('table > thead').css('cursor', 'default');
    }
  }

  updateColumnWidths (): void {
    const tableWidth: number = this.dataTableElement!.offsetWidth - this.sumOfFixedWidthColumnWidths();
    const minColWidthPcs: Immutable.List<number> = getMinimumColumnWidthPercentages(this.props.tableColumns, tableWidth);

    const columnWidths: Immutable.List<string> = applyMinimumColumnWidths(minColWidthPcs, this.state.columnWidths);

    // Persist column widths in state
    if (!Immutable.is(columnWidths, this.state.columnWidths)) {
      this.setState({
        columnWidths: columnWidths
      });
      if (this.props.onColumnWidthsChange) {
        this.props.onColumnWidthsChange(columnWidths);
      }
    }
  }

  scrollSelectionToVisible (): void {
    const { selection, selectionIndexes,
      rowHeight } = (this.props as PropsWithDefaults<T>);

    console.assert(this.props.rowHeight !== -1, 'scrollSelectionToVisible called when no row height was set');

    if (selection.isEmpty()) {
      return;
    }
    let selectionIndex: number = -1;
    if (!this.selectionIndexes.isEmpty()) {
      selectionIndex = this.selectionIndexes.get(0) ?? -1;
    } else if (!selectionIndexes.isEmpty()) {
      for (const i of selectionIndexes.values()) {
        if (i >= 0) {
          selectionIndex = i;
          break;
        }
      }
    }
    if (selectionIndex < 0) {
      return;
    }

    const scrollTop: number = $(this.dataTableContainerElement!).scrollTop() ?? 0;
    const containerHeight: number = this.dataTableContainerElement!.offsetHeight;

    const rowsAbove: number = Math.floor(scrollTop / rowHeight);
    const visibleRows: number = Math.ceil(containerHeight / rowHeight);

    const visibleRange: RangeType = {from: rowsAbove, until: rowsAbove + visibleRows - 1};

    if (selectionIndex <= visibleRange.from || selectionIndex >= visibleRange.until) {
      $(this.dataTableContainerElement!).scrollTop((selectionIndex - Math.max(0, (Math.floor(visibleRows / 2) - 1))) * rowHeight);
    }
  }

  scrollHighlightToVisible (): void {
    const { rowHeight } = (this.props as PropsWithDefaults<T>);

    console.assert(this.props.rowHeight !== -1, 'scrollHighlightToVisible called when no row height was set');

    if (this.props.highlightIndex != null) {
      const highlightIndex: number | Immutable.Set<number> = this.props.highlightIndex;
      if (typeof highlightIndex === 'number' || (Immutable.isSet(highlightIndex) && highlightIndex.size > 0)) {
        const firstHighlightIndex = Immutable.isSet(highlightIndex) ? highlightIndex.first() : highlightIndex;
        const scrollTop: number = $(this.dataTableContainerElement!).scrollTop() ?? 0;
        const containerHeight: number = this.dataTableContainerElement!.offsetHeight;

        const rowsAbove: number = Math.floor(scrollTop / rowHeight);
        const visibleRows: number = Math.ceil(containerHeight / rowHeight);

        const visibleRange: RangeType = {from: rowsAbove, until: rowsAbove + visibleRows - 1};

        if (firstHighlightIndex != null && (firstHighlightIndex <= visibleRange.from || firstHighlightIndex >= visibleRange.until)) {
          const newScrollTopValue: number = (firstHighlightIndex - Math.max(0, (Math.floor(visibleRows / 2) - 1))) * rowHeight;
          // delay scroll to highlight to solve problem with scroll to position done before element is present in table
          setTimeout(() => {
            if (this.dataTableContainerElement != null) {
              $(this.dataTableContainerElement).scrollTop(newScrollTopValue);
            }
          }, 500);
        }
      }
    }
  }

  handleScroll (): void {
    const { rowHeight } = (this.props as PropsWithDefaults<T>);

    if (this.scrollTimer != null) {
      clearTimeout(this.scrollTimer);
    }
    if ($('.esko_tableaction_dropdownmenu-wrapper') != null) {
      $('.esko_tableaction_dropdownmenu-wrapper').removeClass('esko_open');
    }
    if (rowHeight !== -1) {
      this.scrollTimer = setTimeout(this.updateViewParams, 200);
    }
  }

  handleRowClick (id: string, event?: React.MouseEvent<HTMLTableRowElement>): void {
    if (this.props.onItemSelect) {
      this.props.onItemSelect(id);
    }
  }

  handleRowHover (sourceItem: any, targetItem: T, hoverPosition: string): void {
    if (this.props.onHover) {
      this.props.onHover(sourceItem, targetItem, hoverPosition);
    }
  }

  handleRowDrop (sourceItem: any, targetItem: T, dropPosition: string): void {
    if (this.props.onDrop) {
      this.props.onDrop(sourceItem, targetItem, dropPosition);
    }
  }

  handleColumnHeaderMouseEnter (columnIndex: number, event?: React.MouseEvent<HTMLTableCellElement>): void {
    this.hoverColumnHeader = columnIndex;
    this.updateHeaderCursor();
  }

  handleColumnHeaderMouseLeave (columnIndex: number, event?: React.MouseEvent<HTMLTableCellElement>): void {
    this.hoverColumnHeader = -1;
    this.updateHeaderCursor();
  }

  handleColumnHeaderMouseMove (columnIndex: number, event?: React.MouseEvent<HTMLTableCellElement>): void {
    if (event == null) {
      return;
    }

    const { tableColumns } = this.props;
    const offset: {top: number, left: number} = $(event.currentTarget).offset() ?? { top: 0, left: 0 };
    const x: number = (event.pageX || 0) - offset.left;

    this.hoverColumnHeader = columnIndex;

    let hoverColumnHeaderArea: string = '';
    if (x < 10 && columnIndex > 0 && this.isColumnResizable(columnIndex - 1)) {
      hoverColumnHeaderArea = '';
      // Check if self or any column to the right is resizable
      for (let i = columnIndex; i < tableColumns.size; i++) {
        if (this.isColumnResizable(i)) {
          hoverColumnHeaderArea = 'col-resize-left';
          break;
        }
      }
    } else if ((x > (event.currentTarget.offsetWidth || 0) - 10) && (columnIndex < tableColumns.size - 1) && this.isColumnResizable(columnIndex)) {
      hoverColumnHeaderArea = '';
      // Check if any column to the right is resizable
      for (let i = columnIndex + 1; i < tableColumns.size; i++) {
        if (this.isColumnResizable(i)) {
          hoverColumnHeaderArea = 'col-resize-right';
          break;
        }
      }
    }

    if (this.hoverColumnHeaderArea !== hoverColumnHeaderArea) {
      this.hoverColumnHeaderArea = hoverColumnHeaderArea;
      this.updateHeaderCursor();
    }
  }

  handleColumnHeaderMouseDown (columnIndex: number, event?: React.MouseEvent<HTMLTableCellElement>): void {
    this.preventSortOrderChange = this.hoverColumnHeaderArea !== '';
  }

  handleColumnHeaderClick (columnIndex: number, event?: React.MouseEvent<HTMLTableCellElement>): void {
    const { tableColumns, sortOrder, onSortOrderChange } = (this.props as PropsWithDefaults<T>);

    if (onSortOrderChange && this.hoverColumnHeaderArea === '' && !this.preventSortOrderChange) {
      const tableColumn: TableColumn<T> | undefined = tableColumns.get(columnIndex);

      if (tableColumn != null && tableColumn.sortOrderKeyword != null && tableColumn.sortOrderKeyword.length > 0) {
        let newSortOrder: SortOrder = sortOrder;
        if (tableColumn.sortOrderKeyword === sortOrder.keyword) {
          newSortOrder = sortOrder.set('ascending', !sortOrder.ascending);
        } else {
          const ascending = this.sortPreferences.getAscending(tableColumn.sortOrderKeyword);
          newSortOrder = sortOrder.set('keyword', tableColumn.sortOrderKeyword).set('ascending', ascending);
        }
        onSortOrderChange(newSortOrder);
      }
    }
  }

  handleMouseDown (event: JQuery.MouseDownEvent<Document>): void {
    this.resizeColumn = -1;
    this.resizeColumnStartX = -1;
    this.resizeColumnStartWidths = Immutable.List();
    this.columnWidths = Immutable.List();

    this.reorderColumn = -1;
    this.reorderColumnMouseStartX = -1;
    this.reorderColumnStartX = -1;
    this.reorderColumnWidth = 0;
    if (this.state.reorderColumnActive) {
      this.setState({
        reorderColumnActive: false
      });
    }

    if (this.hoverColumnHeader >= 0) {
      if (this.hoverColumnHeaderArea === 'col-resize-right') {
        this.resizeColumn = this.hoverColumnHeader;
      } else if (this.hoverColumnHeaderArea === 'col-resize-left') {
        this.resizeColumn = this.hoverColumnHeader - 1;
      } else {
        if (this.props.onMoveColumn) {
          const tableColumn: TableColumn<T> | undefined = this.props.tableColumns.get(this.hoverColumnHeader);
          if (tableColumn != null && tableColumn.movable) {
            this.reorderColumn = this.hoverColumnHeader;
          }
        }
      }
    }

    if (this.reorderColumn >= 0) {
      event.preventDefault();

      this.reorderColumnMouseStartX = event.pageX || 0;
      const th = $(this.headerRootElement!).find('table > thead > tr > th:nth-child(' + (this.reorderColumn + 1) + ')');
      this.reorderColumnStartX = (th.offset()?.left ?? 0) - (th.parent().offset()?.left ?? 0);
      this.reorderColumnWidth = th.outerWidth() ?? 0;
    }

    if (this.resizeColumn >= 0) {
      event.preventDefault();

      this.resizeColumnStartX = event.pageX || 0;
      this.resizeColumnStartWidths = this.state.columnWidths.map((width: string) => {
        if (width.endsWith('%')) {
          return parseFloat(width);
        } else {
          return -1;
        }
      });
      this.columnWidths = this.state.columnWidths;
    }
  }

  _setColumnWidth (index: number, newWidthString: string): void {
    $(this.headerRootElement!).find('table > thead > tr > th:nth-child(' + (index + 1) + ')').css('width', newWidthString);
    $(this.dataTableContainerElement!).find('table > tbody > tr:first-child > td:nth-child(' + (index + 1) + ')').css('width', newWidthString);
    this.columnWidths = this.columnWidths.set(index, newWidthString);
  }

  handleMouseMove (event: JQuery.MouseMoveEvent<Document>): void {
    if (this.reorderColumn >= 0) {
      if (!this.state.reorderColumnActive) {
        const dx: number = (event.pageX || 0) - this.reorderColumnMouseStartX;
        if (dx <= -5 || dx >= 5) {
          this.setState({
            reorderColumnActive: true
          });
        }
      } else {
        if (this.reorderColumnElement != null) {
          const dx: number = (event.pageX || 0) - this.reorderColumnMouseStartX;

          let reorderColumnX: number = Math.max(this.reorderColumnStartX + dx, 0);
          const tableWidth: number = this.dataTableElement!.offsetWidth;
          const maxX: number = tableWidth - this.reorderColumnWidth;
          reorderColumnX = Math.min(reorderColumnX, maxX);

          if (dx < 0) {
            let prevColumn: number = -1;
            for (let i = this.reorderColumn - 1; i >= 0; i--) {
              const tableColumn: TableColumn<T> | undefined = this.props.tableColumns.get(i);
              if (tableColumn != null && tableColumn.movable) {
                prevColumn = i;
                break;
              }
            }
            if (prevColumn >= 0) {
              const prevTh = $(this.headerRootElement!).find('table > thead > tr > th:nth-child(' + (prevColumn + 1) + ')');
              const prevColumnX: number = (prevTh.offset()?.left ?? 0) - (prevTh.parent().offset()?.left ?? 0);
              const prevColumnWidth: number = prevTh.outerWidth() ?? 0;
              if (reorderColumnX < (prevColumnX + (prevColumnWidth / 2))) {
                if (this.props.onMoveColumn) {
                  const reorderColumn: number = this.reorderColumn;
                  this.reorderColumn = prevColumn;
                  this.reorderColumnMouseStartX = event.pageX || 0;
                  this.reorderColumnStartX = reorderColumnX;
                  this.props.onMoveColumn(reorderColumn, prevColumn);
                }
              }
            }
          } else if (dx > 0) {
            let nextColumn: number = this.props.tableColumns.size;
            for (let i = this.reorderColumn + 1; i < this.props.tableColumns.size; i++) {
              const tableColumn: TableColumn<T> | undefined = this.props.tableColumns.get(i);
              if (tableColumn != null && tableColumn.movable) {
                nextColumn = i;
                break;
              }
            }
            if (nextColumn < this.props.tableColumns.size) {
              const nextTh = $(this.headerRootElement!).find('table > thead > tr > th:nth-child(' + (nextColumn + 1) + ')');
              const nextColumnX: number = (nextTh.offset()?.left ?? 0) - (nextTh.parent().offset()?.left ?? 0);
              const nextColumnWidth: number = nextTh.outerWidth() ?? 0;
              if ((reorderColumnX + this.reorderColumnWidth) > (nextColumnX + (nextColumnWidth / 2))) {
                if (this.props.onMoveColumn) {
                  const reorderColumn: number = this.reorderColumn;
                  this.reorderColumn = nextColumn;
                  this.reorderColumnMouseStartX = event.pageX || 0;
                  this.reorderColumnStartX = reorderColumnX;
                  this.props.onMoveColumn(reorderColumn, nextColumn);
                }
              }
            }
          }

          if (this.reorderColumnElement != null) {
            const _reorderColumnElement: HTMLDivElement = this.reorderColumnElement;
            $(_reorderColumnElement).css('left', reorderColumnX + 'px');
          }
        }
      }
    }

    if (this.resizeColumn >= 0) {
      // calculate width of resize column
      const tableWidth: number = this.dataTableElement!.offsetWidth - this.sumOfFixedWidthColumnWidths();
      const minColWidthPcs: Immutable.List<number> = getMinimumColumnWidthPercentages(this.props.tableColumns, tableWidth);

      // don't change the width of the columns before,
      // just calculate the combined size of all columns
      // before so we know how much space we have left
      let withBeforeResizeColumnPercentage: number = 0;
      for (let columnIndex: number = 0; columnIndex < this.resizeColumn; columnIndex++) {
        const startWidthPercentage: number = this.resizeColumnStartWidths.get(columnIndex) ?? 0;
        withBeforeResizeColumnPercentage += startWidthPercentage;
      }

      const columnMinWidthPercentage: number = Math.round(35 * 10000 / tableWidth) / 100; // minimum width = 35px

      const columnAfterStartIndex: number = this.resizeColumn + 1;
      const numberOfColumnsAfter: number = (this.props.tableColumns.size - columnAfterStartIndex);

      const minResizeWidthPercentage: number = minColWidthPcs.get(this.resizeColumn) ?? 0;
      const maxResizeWidthPercentage: number = 100 - withBeforeResizeColumnPercentage - minResizeWidthPercentage - (numberOfColumnsAfter * columnMinWidthPercentage);

      const resizeDistancePercentage: number = ((event.pageX || 0) - this.resizeColumnStartX) * 100 / tableWidth;

      const startResizeWidthPercentage: number = this.resizeColumnStartWidths.get(this.resizeColumn) ?? 0;

      // update width of resize column
      let resizedWidthPercentage: number = Math.min(Math.max(startResizeWidthPercentage + resizeDistancePercentage, minResizeWidthPercentage), maxResizeWidthPercentage);
      resizedWidthPercentage = Math.round(resizedWidthPercentage * 100) / 100;
      if (Math.round(resizedWidthPercentage * 100) !== Math.round(startResizeWidthPercentage * 100)) {
        this._setColumnWidth(this.resizeColumn, resizedWidthPercentage + '%');
      }

      // update width of columns after resize column
      let totalRemainingExistingColumnSize: number = 0;
      for (let columnAfterIndex: number = columnAfterStartIndex; columnAfterIndex < this.props.tableColumns.size; columnAfterIndex++) {
        totalRemainingExistingColumnSize += this.resizeColumnStartWidths.get(columnAfterIndex) ?? 0;
      }
      const avgExistingColumnSize: number = totalRemainingExistingColumnSize / numberOfColumnsAfter;

      const remainingWidth: number = 100 - withBeforeResizeColumnPercentage - resizedWidthPercentage;
      const avgExpectedColumnWidth: number = remainingWidth / numberOfColumnsAfter;

      for (let columnAfterIndex: number = columnAfterStartIndex; columnAfterIndex < this.props.tableColumns.size; columnAfterIndex++) {
        const startWidthPercentage: number = this.resizeColumnStartWidths.get(columnAfterIndex) ?? 0;

        const columnAfterWidth: number = (avgExistingColumnSize > 0
          ? Math.max(columnMinWidthPercentage, startWidthPercentage * avgExpectedColumnWidth / avgExistingColumnSize)
          : columnMinWidthPercentage
        );

        if (Math.round(columnAfterWidth * 100) !== Math.round(startWidthPercentage * 100)) {
          const columnWidthPercentage = Math.round(columnAfterWidth * 100) / 100;
          this._setColumnWidth(columnAfterIndex, columnWidthPercentage + '%');
        }
      }
    }
  }

  handleMouseUp (event: JQuery.MouseUpEvent<Document>): void {
    if (this.reorderColumn >= 0) {
      this.reorderColumn = -1;
      this.reorderColumnMouseStartX = -1;
      this.reorderColumnStartX = -1;
      this.reorderColumnWidth = 0;
      this.setState({
        reorderColumnActive: false
      });
    }

    if (this.resizeColumn >= 0) {
      this.resizeColumn = -1;
      this.resizeColumnStartX = -1;
      this.resizeColumnStartWidths = Immutable.List();

      // Persist column widths in state
      if (!Immutable.is(this.columnWidths, this.state.columnWidths)) {
        this.setState({
          columnWidths: this.columnWidths
        });
        if (this.props.onColumnWidthsChange) {
          this.props.onColumnWidthsChange(this.columnWidths);
        }
      }

      this.columnWidths = Immutable.List();
    }
  }

  handleWindowResize () {
    this.updateHeaderWidth();
    this.updateColumnWidths();
  }

  sumOfFixedWidthColumnWidths (): number {
    return sumOfFixedWidthColumnWidths(this.state.columnWidths);
  }

  isColumnResizable (index: number): boolean {
    const { columnWidths } = this.state;
    if (index >= 0 && index < columnWidths.size && columnWidths.get(index)?.endsWith('%')) {
      return true;
    }
    return false;
  }

  override componentDidMount (): void {
    const { rowHeight, isInitialized, rowData, ensureSelectionIsVisible,
      onEnsuredSelectionIsVisible, highlightIndex, setFocus } = (this.props as PropsWithDefaults<T>);
    if (rowHeight !== -1) {
      if (isInitialized) {
        this._updateViewParams(true);
      }
      if (rowData.size > 0) {
        this.updateHeaderWidth();

        if (ensureSelectionIsVisible) {
          this.scrollSelectionToVisible();

          if (onEnsuredSelectionIsVisible != null) {
            const _onEnsuredSelectionIsVisible = onEnsuredSelectionIsVisible;
            setTimeout(() => _onEnsuredSelectionIsVisible(), 0);
          }
        }

        if (highlightIndex != null && (typeof highlightIndex === 'number' ||
          (highlightIndex instanceof Immutable.Set && highlightIndex.size > 0))) {
          this.scrollHighlightToVisible();
        }
      }
    }

    // Start listening
    window.addEventListener('resize', this.handleWindowResize);
    $(document).mousedown(this.handleMouseDown);
    $(document).mousemove(this.handleMouseMove);
    $(document).mouseup(this.handleMouseUp);
    if (this.dataTableContainerElement != null) {
      this.dataTableContainerElement.addEventListener('scroll', this.handleScroll);
      if (setFocus) {
        this.dataTableContainerElement.focus();
      }
    }
  }

  updateState (prevProps: Props<T>) {
    const { rowData, rowHeight, isInitialized, ensureSelectionIsVisible,
      onEnsuredSelectionIsVisible, highlightIndex } = (this.props as PropsWithDefaults<T>);

    if (rowHeight !== -1) {
      if (isInitialized) {
        this._updateViewParams(!(prevProps.isInitialized ?? true));
      }

      this.updateHeaderWidth();

      if (rowData.size > 0) {
        if (ensureSelectionIsVisible) {
          this.scrollSelectionToVisible();

          if (onEnsuredSelectionIsVisible != null) {
            const _onEnsuredSelectionIsVisible = onEnsuredSelectionIsVisible;
            setTimeout(() => _onEnsuredSelectionIsVisible(), 0);
          }
        }
        if ((typeof highlightIndex === 'number' ||
            (highlightIndex instanceof Immutable.Set && highlightIndex.size > 0)) &&
            (highlightIndex !== prevProps.highlightIndex || prevProps.rowData.size === 0)) {
          this.scrollHighlightToVisible();
        }
      }
    }

    const { tableColumns } = this.props;

    // Check if tableColumns prop changed
    let changed: boolean = false;
    if (tableColumns.size !== prevProps.tableColumns.size) {
      changed = true;
    } else {
      for (let i = 0; i < tableColumns.size; i++) {
        if (tableColumns.get(i)?.name !== prevProps.tableColumns.get(i)?.name) {
          changed = true;
          break;
        }
      }
    }

    if (changed && this.dataTableElement != null) {
      let columnWidths: Immutable.List<string> = normalizeColumnWidths(tableColumns);
      const tableWidth: number = this.dataTableElement.offsetWidth - sumOfFixedWidthColumnWidths(columnWidths);
      const minColWidthPcs: Immutable.List<number> = getMinimumColumnWidthPercentages(tableColumns, tableWidth);
      columnWidths = applyMinimumColumnWidths(minColWidthPcs, columnWidths);
      this.setState({
        columnWidths: columnWidths
      });
    }
  }

  override componentDidUpdate (prevProps: Readonly<Props<T>>): void {
    if (this.props.setVisibleRange !== prevProps.setVisibleRange) {
      this._setVisibleRange = this.props.setVisibleRange != null ? _.throttle(this.props.setVisibleRange, 50) : null;
    }

    this.updateState(prevProps);
  }

  override componentWillUnmount (): void {
    // Stop listening
    window.removeEventListener('resize', this.handleWindowResize);
    $(document).off('mousedown', this.handleMouseDown);
    $(document).off('mousemove', this.handleMouseMove);
    $(document).off('mouseup', this.handleMouseUp);
    this.dataTableContainerElement?.removeEventListener('scroll', this.handleScroll);
  }

  renderDataComponent (content: React.ReactNode, rowIndex: number, columnIndex: number, columnWidth: string, alignRight: boolean) {
    const { rowHeight } = (this.props as PropsWithDefaults<T>);
    const { reorderColumnActive } = this.state;

    const tdStyle = rowIndex === 0 ? {width: columnWidth} : undefined;

    let actualContent = content;
    if (reorderColumnActive && this.reorderColumn >= 0 && this.reorderColumn === columnIndex) {
      actualContent = ' ';
    }

    return (
      <td key={'drowcol' + rowIndex + '-' + columnIndex}
        style={tdStyle}
        className={classNames('esko_tabledatacell', {esko_tabledatacellalignright: alignRight})}
      >
        <div style={{height: rowHeight !== -1 ? rowHeight + 'px' : undefined}}>
          {actualContent}
        </div>
      </td>
    );
  }

  renderReorderColumnComponent () {
    const { tableColumns, sortOrder, rowHeight, rowData } = (this.props as PropsWithDefaults<T>);
    console.assert(rowHeight !== -1, 'renderReorderColumnComponent called when no row height was set');

    const { reorderColumnActive } = this.state;

    if (!reorderColumnActive || this.reorderColumn < 0) {
      this.reorderColumnElement = null;
      return null;
    }

    const tableColumentToReorder: TableColumn<T> | undefined = tableColumns.get(this.reorderColumn);

    const reorderTableColumn: Immutable.List<TableColumn<T>> = tableColumentToReorder != null ? Immutable.List([tableColumentToReorder]) : Immutable.List();

    const divStyle: React.CSSProperties = {
      position: 'absolute',
      width: this.reorderColumnWidth + 'px',
      left: this.reorderColumnStartX + 'px',
      opacity: 0.5,
      borderLeft: '1px solid rgb(180, 180, 180)',
      borderRight: '1px solid rgb(180, 180, 180)',
      zIndex: 10
    };

    const scrollTop: number = $(this.dataTableContainerElement!).scrollTop() ?? 0;
    const reorderColumnTop: number = -(scrollTop % rowHeight);

    const dataTableContainerStyle = {
      width: '100%',
      height: this.dataTableContainerElement!.offsetHeight + 'px',
      overflow: 'hidden'
    };

    const dataTableStyle: React.CSSProperties = {
      width: '100%',
      position: 'relative',
      top: reorderColumnTop + 'px'
    };

    // Create the proper row components for the subset
    const containerHeight: number = this.dataTableContainerElement!.offsetHeight;
    const rowsAbove: number = Math.floor(scrollTop / rowHeight);
    const visibleRows: number = Math.ceil(containerHeight / rowHeight);
    const reorderRowData = rowData.slice(rowsAbove, rowsAbove + visibleRows);
    let rowComponents = reorderRowData.map((row, rowIndex) => {
      let content: React.ReactNode = ' ';
      let alignRight: boolean = false;
      if (row) {
        const tableColumn = reorderTableColumn.get(0);
        if (tableColumn != null) {
          if (tableColumn.renderCell != null){
            content = tableColumn.renderCell(row, rowIndex, this.reorderColumn);
          }
          alignRight = (tableColumn.alignRight != null && tableColumn.alignRight === true);
        }
      }

      return <tr className='esko_tablerow' key={'drow' + rowIndex}>{this.renderDataComponent(content, rowIndex, -1, '100%', alignRight)}</tr>;
    });

    return (
      <div style={{position: 'relative'}}>
        <div ref={(c) => { this.reorderColumnElement = c; }} style={divStyle}>
          <TableHeader<T>
            tableColumns={reorderTableColumn}
            width='100%'
            columnWidths={Immutable.List(['100%'])}
            sortOrder={sortOrder}
          />
          <div className='esko_tabledata' style={dataTableContainerStyle}>
            <table className='esko_tabledata_table' style={dataTableStyle}>
              <tbody>
                {rowComponents}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }

  override render (): React.ReactNode {
    const {
      tableColumns, rowData, realTotalCount, isFetching, selection, showSelection, className,
      sortOrder, emptyTableText, loadingText, allTableColumns, onToggleColumnSelection,
      id, disabledIds, rowHeight, highlightId, isInitialized, noTableHeader, clearHighlightId,
      getClassNameOnDragHover, itemCanDragDrop, itemGetDragDropItemType, itemAcceptsDropItemTypes,
      language // eslint-disable-line no-unused-vars
    } = (this.props as PropsWithDefaults<T>);
    const { columnWidths, reorderColumnActive } = this.state;

    this.selectionIndexes = Immutable.List();

    if (this.props.sortPreferences != null) {
      this.sortPreferences = this.props.sortPreferences;
    }
    if (sortOrder.keyword != null && sortOrder.keyword.length > 0) {
      this.sortPreferences = this.sortPreferences
        .set('activeKeyword', sortOrder.keyword)
        .setAscending(sortOrder.keyword, sortOrder.ascending);
    }

    const visibleFrom: number = Math.max(0, this.visibleRange.from - 10);
    const visibleUntil: number = visibleFrom + 50;

    const rowsAboveDataWindow: number = Math.min(visibleFrom, rowData.size);
    const dataWindowEnd: number = Math.min(visibleUntil, rowData.size);

    let rowComponents = [];

    if (!isInitialized) {
      rowComponents = [(
        <tr key='drow0' className='esko_tablerowempty'>
          <td className='esko_tabledataempty'>
            <Spinner small label={translate(loadingText)} />
          </td>
        </tr>
      )];
    } else {
      if (rowsAboveDataWindow > 0) {
        rowComponents.push(
          <tr key='drow0' className='esko_tablerow esko_tablerow_nohover' style={{height: (rowHeight !== -1 ? (rowsAboveDataWindow * rowHeight) : undefined)}}>
            {tableColumns.map((tableColumn, columnIndex) =>
              <td key={'drowcol0-' + columnIndex}
                style={{width: columnWidths.get(columnIndex)}}
                className={classNames('esko_tabledatacell',
                  {esko_tabledatacellalignright: (tableColumn.alignRight != null && tableColumn.alignRight === true)})} />
            )}
          </tr>
        );
      }

      for (let rowIndex = rowsAboveDataWindow; rowIndex < dataWindowEnd; rowIndex++) {
        const currentRow: any = rowData.get(rowIndex);
        let selected: boolean = false;
        let isHighLighted: boolean = false;
        let rowDisabled: boolean = false;
        if (currentRow != null && currentRow.id != null) {
          selected = this._isSelected(currentRow, selection);
          if (selected) {
            this.selectionIndexes = this.selectionIndexes.push(rowIndex);
          }

          if (!selected && rowIndex >= this.visibleRange.from && rowIndex < this.visibleRange.until) {
            isHighLighted = _isHighlighted(highlightId, currentRow.id);
          }

          rowDisabled = disabledIds.includes(currentRow.id);
        }
        rowComponents.push(
          <TableRow<T>
            key={currentRow != null && currentRow.id != null ? 'drow_id_' + currentRow.id : 'drow' + rowIndex}
            row={currentRow}
            rowIndex={rowIndex}
            disabled={rowDisabled}
            onClick={rowDisabled ? () => {} : this.handleRowClick}
            onHover={rowDisabled ? () => {} : this.handleRowHover}
            onDrop={this.handleRowDrop}
            selected={!!showSelection && selected && !rowDisabled}
            showSelection={showSelection}
            highlight={isHighLighted}
            tableColumns={tableColumns}
            columnWidths={columnWidths}
            rowHeight={rowHeight === -1 ? undefined : rowHeight}
            reorderColumnActive={reorderColumnActive}
            reorderColumn={this.reorderColumn}
            clearHighlight={clearHighlightId}
            getClassNameOnDragHover={getClassNameOnDragHover}
            itemCanDragDrop={itemCanDragDrop}
            itemGetDragDropItemType={itemGetDragDropItemType}
            itemAcceptsDropItemTypes={itemAcceptsDropItemTypes}
          />
        );
      }

      if (dataWindowEnd < rowData.size) {
        const rowsAfterVisibleRange: number = rowData.size - dataWindowEnd;
        rowComponents.push(
          <tr key='drow_last' className='esko_tablerow' style={{height: (rowHeight !== -1 ? (rowsAfterVisibleRange * rowHeight) : undefined)}} />
        );
      }

      if (rowComponents.length === 0) {
        if (isFetching) {
          rowComponents = [(
            <tr key='drow0' className='esko_tablerowempty'>
              <td className='esko_tabledataempty'>
                <Spinner small label={translate(loadingText)} />
              </td>
            </tr>
          )];
        } else {
          rowComponents = [(
            <tr key='drow0' className='esko_tablerowempty'>
              <td className='esko_tabledataempty'>
                <span>{translate(emptyTableText)}</span>
              </td>
            </tr>
          )];
        }
      } else if (realTotalCount != null && realTotalCount > rowData.size) {
        rowComponents.push(
          <tr key='drowMore' className='esko_tablerowmoreitems esko_tablerow_nohover'>
            <td className='esko_tablemoreitems' colSpan={tableColumns.size}>
              <span>{translate('shared.table.morerows', ((realTotalCount || 0) - rowData.size), rowData.size)}</span>
            </td>
          </tr>
        );
      }
    }

    const reorderColumn = reorderColumnActive && this.reorderColumn >= 0 ? this.reorderColumn : -1;

    return (
      <div id={id} className={classNames(className, 'esko_table')}>
        {this.props.rowHeight !== -1 ? this.renderReorderColumnComponent() : null}
        { !noTableHeader && <TableHeader<T>
          tableColumns={tableColumns}
          width={this.state.headerWidth}
          columnWidths={columnWidths}
          sortOrder={sortOrder}
          allTableColumns={allTableColumns}
          reorderColumn={reorderColumn}
          onColumnHeaderMouseEnter={this.handleColumnHeaderMouseEnter}
          onColumnHeaderMouseLeave={this.handleColumnHeaderMouseLeave}
          onColumnHeaderMouseMove={this.handleColumnHeaderMouseMove}
          onColumnHeaderMouseDown={this.handleColumnHeaderMouseDown}
          onColumnHeaderClick={this.handleColumnHeaderClick}
          onToggleColumnSelection={onToggleColumnSelection}
          headerRootRef={(el) => { this.headerRootElement = el; }} />
        }
        <div className='esko_tabledata' tabIndex={-1} ref={(divElement) => { this.dataTableContainerElement = divElement; }}>
          <table className='esko_tabledata_table' style={{width: '100%'}} ref={(tableElement) => { this.dataTableElement = tableElement; }}>
            <tbody>
              {rowComponents}
            </tbody>
          </table>
        </div>
      </div>
    );
  }
}

export default Table;
